import type { Chat } from '@/data/types';
import { onePerCustomer, type ChannelPair } from '@/lib/channels';
import { clearFilter, filterCount, filterValues, toggleFilter, useListFilter, type FilterGroup } from '@/lib/listFilter';

const GROUPS: { key: FilterGroup; title: string }[] = [
  { key: 'brand', title: 'Number' },
  { key: 'status', title: 'Status' },
  { key: 'owner', title: 'Owner' },
  { key: 'area', title: 'Area' },
  { key: 'label', title: 'Labels' },
];

const STATUS_ORDER = ['Unpaid', 'Hot', 'Warm', 'Cold', 'New', 'Platinum', 'Gold', 'Silver', 'Bronze', 'Amber'];

/** The chat list's filter (Gary, 18 Oct): the values on the chats you can see, with how many have each. */
export function FilterSheet({ chats, pairs }: { chats: Chat[]; pairs: Map<string, ChannelPair> }) {
  const filter = useListFilter();
  const having: Record<FilterGroup, Map<string, Chat[]>> = { show: new Map(), brand: new Map(), status: new Map(), owner: new Map(), area: new Map(), label: new Map() };
  for (const c of chats) {
    const v = filterValues(c);
    for (const g of GROUPS) for (const x of v[g.key]) having[g.key].set(x, [...(having[g.key].get(x) ?? []), c]);
  }
  // Counted as the list shows them, one per customer (mobile review, 19 Oct: All open chats said 25 over 24 rows).
  const now = Date.now();
  const counts = Object.fromEntries(GROUPS.map((g) => [g.key, new Map([...having[g.key]].map(([x, cs]) => [x, onePerCustomer(cs, pairs, now).length]))])) as Record<FilterGroup, Map<string, number>>;
  const values = (g: FilterGroup) => {
    // Picked values stay listed even when no chat has them any more, so they can be cleared.
    const all = [...new Set([...counts[g].keys(), ...filter[g]])];
    return g === 'status'
      ? all.sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b))
      : all.sort((a, b) => a.localeCompare(b));
  };
  return (
    <>
      <h2 className="chats-sheet-title">Filter chats</h2>
      <p className="chats-sheet-hint">Narrows the list to the chats you pick. Kept on this device.</p>
      {/* Each group a heading and its values as the list's own chips, ticked ones filled (Gary, 18 Oct:
          small group titles over tall rows looked odd). */}
      {/* Number only when both brands' chats are in the list, or one is picked (so it can be cleared). */}
      {GROUPS.map((g) => values(g.key).length > (g.key === 'brand' ? 1 : 0) && (
        <section key={g.key} className="chats-filter-group">
          <h3 className="chats-filter-group-title">{g.title}</h3>
          <div className="chats-filter-options">
            {values(g.key).map((x) => {
              const on = filter[g.key].includes(x);
              return (
                <button key={x} type="button" className={`chats-chip${on ? ' active' : ''}`} aria-pressed={on} onClick={() => toggleFilter(g.key, x)}>
                  {x}<span className="chats-filter-count">{counts[g.key].get(x) ?? 0}</span>
                </button>
              );
            })}
          </div>
        </section>
      ))}
      {filterCount(filter) > 0 && (
        <button type="button" className="chats-btn chats-btn--ghost chats-filter-clear" onClick={clearFilter}>Clear filter</button>
      )}
    </>
  );
}
